import { z } from "zod";
const CROCKFORD = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
const ULID_RE = /^[0-9A-HJKMNP-TV-Z]{26}$/;
export const IdSchema = z.string().regex(ULID_RE, "must be a ULID");
export function isId(value) {
    return typeof value === "string" && ULID_RE.test(value);
}
/**
 * Creates a ULID generator. Inject `now`/`random` for deterministic tests.
 * Ids from one generator are strictly increasing, even within the same millisecond.
 */
export function createIdGenerator(options = {}) {
    const now = options.now ?? (() => Date.now());
    const random = options.random ??
        ((bytes) => {
            globalThis.crypto.getRandomValues(bytes);
        });
    let lastTime = -1;
    let lastRandom = [];
    return () => {
        const time = now();
        if (!Number.isSafeInteger(time) || time < 0 || time > 0xffffffffffff) {
            throw new RangeError("ULID time must be a 48-bit non-negative integer");
        }
        let rand;
        if (time <= lastTime) {
            rand = increment(lastRandom);
        }
        else {
            const bytes = new Uint8Array(16);
            random(bytes);
            rand = Array.from(bytes, (b) => b % 32);
            lastTime = time;
        }
        lastRandom = rand;
        return encodeTime(Math.max(time, lastTime)) + rand.map((d) => CROCKFORD[d]).join("");
    };
}
function encodeTime(time) {
    let out = "";
    let t = time;
    for (let i = 0; i < 10; i++) {
        out = CROCKFORD[t % 32] + out;
        t = Math.floor(t / 32);
    }
    return out;
}
function increment(digits) {
    const next = [...digits];
    for (let i = next.length - 1; i >= 0; i--) {
        if (next[i] < 31) {
            next[i] = next[i] + 1;
            return next;
        }
        next[i] = 0;
    }
    throw new RangeError("ULID random component overflow");
}
/** Default process-wide id generator. */
export const newId = createIdGenerator();
/**
 * Namespaced dotted name, e.g. `org.building`, `crm.createContact`, `intercompany.contract.sign`.
 * Used for entity type names, tool names, permission names and event types.
 */
export const NamespacedNameSchema = z
    .string()
    .regex(/^[a-z][a-z0-9]*(?:\.[a-z][a-zA-Z0-9]*)+$/, "must be a namespaced name like 'org.floor'");
export const TypeNameSchema = NamespacedNameSchema;
/** Module id: lowercase, digits and dashes, e.g. "org", "intercompany", "voice". */
export const ModuleIdSchema = z.string().regex(/^[a-z][a-z0-9-]*$/, "must be a module id");
//# sourceMappingURL=ids.js.map